import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import { motion } from "framer-motion";
import { COLORS } from "../../../Constants/colors";
import featureImg from "../../../Assets/ai_ml_feature.png"; 
import applicationImg from "../../../Assets/ai_ml_applications.png";
import KEYWORDS from "../../../Constants/keywords";


// ===== Styled Components =====
const PageWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 3rem 2rem;
    background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.1),
        rgba(236, 72, 153, 0.1)
    );
    color: ${COLORS.textPrimary};
    overflow-x: hidden;

`;

// ===== Updated Hero Section =====
const Hero = styled.section`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 4rem 2rem;
    background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.1),
        rgba(236, 72, 153, 0.1)
    );
    border-radius: 20px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;

const HeroHeading = styled(motion.h1)`
    font-size: 2.8rem;
    font-weight: 800;
    line-height: 1.2;
    background: linear-gradient(90deg, ${COLORS.primary}, ${COLORS.accent});
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        font-size: 2rem;
    }
`;

const HeroSubtext = styled(motion.p)`
    font-size: 1.2rem;
    color: ${COLORS.textSecondary};
    max-width: 700px;
    margin: 0 auto;
    line-height: 1.6;
`;

const SectionTitle = styled.h2`
    font-size: 2rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: ${COLORS.primary};
    text-align: center;
`;

const FeatureGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1.5rem;
`;

const FeatureCard = styled(motion.div)`
    background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.1),
        rgba(236, 72, 153, 0.1)
    );
    border-radius: 16px;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    align-items: flex-start;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transition: transform 0.3s ease;
    &:hover {
        transform: translateY(-6px);
    }
    h3 {
        font-size: 1.2rem;
        color: ${COLORS.primary};
    }
    p {
        font-size: 1.1rem;
        color: ${COLORS.textSecondary};
    }
`;

const SectionImage = styled(motion.div)`
    max-width: 900px;
    margin: 2rem auto;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);

    img {
        width: 100%;
        height: auto;
        display: block;
        object-fit: cover;
        border-radius: 16px;
        transition: transform 0.4s ease;
    }

    &:hover img {
        transform: scale(1.03);
    }

    @media (max-width: 768px) {
        max-width: 95%;
    }
`;

const ApplicationsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 1.5rem;
`;

const ApplicationCard = styled(motion.div)`
    background: linear-gradient(
        135deg,
        rgba(99, 102, 241, 0.1),
        rgba(236, 72, 153, 0.1)
    );
    border-radius: 16px;
    padding: 1.5rem;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
    h4 {
        font-size: 1.2rem;
        color: ${COLORS.primary};
        margin-bottom: 0.5rem;
    }
    p {
        font-size: 1.1rem;
        color: ${COLORS.textSecondary};
    }
`;

// ===== Data =====
const features = [
  {
        title: "Predictive Analytics & Decision Automation",
        description: "ML models for Forecasting, Process Optimisation",
    },
    {
        title: "Natural Language Processing (NLP)",
        description: "Chatbots, Document Summarisation, Semantic search, Generative AI",
    },
    {
        title: "Computer Vision & Image Analytics",
        description: "Object detection, Image classification, Segmentation, Video Analytics",
    },
    {
        title: "LLM Fine-Tuning, RAG, Local LLM Inference",
        description:
        "Customising models for domain-specific tasks, Building retrieval-augmented generation pipelines, Deploying locally hosted LLMs for privacy and performance",
    },
    {
        title: "Speech & Audio AI",
        description: "Speech recognition, Audio classification, Voice-enabled assistants",
    },
    {
        title: "Deep Reinforcement Learning",
        description: "AI agents for Autonomous decision-making",
    },
    {
        title: "Generative & Representation Learning",
        description: "GANs, Self-supervised learning, Deep generative models",
    },
];

const applications = [
    {
        title: "LLM-powered Intelligent Chatbot",
        description:
        "Analyses historical records and generates actionable insights for decision support",
    },
    {
        title: "Voice-to-Text to Structured Data Pipeline",
        description:
        "Converts unstructured voice inputs into structured, queryable insights",
    },
    {
        title: "Predictive Risk Monitoring Platform",
        description:
        "Forecasts critical event recurrence post high-risk interactions to enable timely interventions",
    },
    {
        title: "Local LLM Deployment for Data Privacy",
        description:
        "Built systems for running locally hosted LLMs to enable secure, low-latency inference without external dependencies",
    },
];

// ===== Component =====
export default function AIMLDetailPage() {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <>
            <Helmet>
                <meta name="keywords" content={KEYWORDS.ai.join(", ")} />
            </Helmet>
            <PageWrapper>
            {/* Hero Section */}
                <Hero>
                    <HeroHeading
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        Artificial Intelligence & Machine Learning
                    </HeroHeading>
                    <HeroSubtext
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.3 }}
                    >
                        Virtuosoft Technologies deliver end-to-end AI solutions spanning predictive
                        analytics, NLP, computer vision, speech AI, deep reinforcement learning, and
                        generative models.
                    </HeroSubtext>
                </Hero>


            {/* Features Section */}
            {/* Features Section */}
                <section>
                    {/* <SectionTitle>AI & ML Capabilities</SectionTitle> */}
                    <SectionImage
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <img src={featureImg} alt="AI Features" />
                    </SectionImage>
                    <FeatureGrid>
                        {features.map((f, idx) => (
                            <FeatureCard
                                key={idx}
                                initial={{ opacity: 0, x: idx % 2 === 0 ? -100 : 100 }} // 👈 alternate left/right
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: false, amount: 0.5 }}
                                transition={{ duration: 0.6, delay: idx * 0.1 }}
                                >
                                <h3>{f.title}</h3>
                                <p>{f.description}</p>
                            </FeatureCard>
                        ))}
                    </FeatureGrid>
                </section>

                {/* Applications Section */}
                <section>
                <SectionImage
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true, amount: 0.3 }}
                        transition={{ duration: 0.6 }}
                    >
                        <img src={applicationImg} alt="AI Applications" />
                    </SectionImage>
                    <SectionTitle>Selected AI Applications Developed for Clients</SectionTitle>
                    <ApplicationsGrid>
                        {applications.map((app, idx) => (
                            <ApplicationCard
                            key={idx}
                            initial={{ opacity: 0, x: idx % 2 === 0 ? -120 : 120 }} // alternate direction
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: false, amount: 0.3 }}
                            transition={{ duration: 0.6, delay: idx * 0.15 }}
                            >
                            <h4>{app.title}</h4>
                            <p>{app.description}</p>
                            </ApplicationCard>
                        ))}
                    </ApplicationsGrid>

                </section>
            </PageWrapper>
        </>

    );
}
